import { useEffect, useMemo } from 'react'
import { RoleType } from '../../models/Role'
import { type BillingCustomer, type PricePeriod } from '../../models/Plan'
import { type SeatPurchase } from '../../store/auth/auth'
import { useCreateInvoicePreviewMutation } from '../../store/plan/plan'
import { hidesBillingInfo, inFreeTrial, isAggregateBilling, isEmailInvoiced } from './billingFacts'
import { newRecurringFee, seatUnitPrice } from './seatUnitPrice'
import { useBillingPlan } from './useBillingPlan'

export type DueLabel = 'today' | 'endOfTrial'

export interface SeatPurchaseLine {
  seatType: RoleType
  quantity: number
  unitAmount: number | null
}

export interface SeatPurchasePreview {
  lines: SeatPurchaseLine[]
  newRecurringFee: number | null
  period?: PricePeriod
  renewsOn?: string
  dueLabel: DueLabel | null
  dueAmount: number | null
  currency: string
  showsAmounts: boolean
  isLoading: boolean
}

const chargesImmediately = (customer?: BillingCustomer) =>
  !!customer?.subscription && !hidesBillingInfo(customer) && !isAggregateBilling(customer) && !isEmailInvoiced(customer)

export const useSeatPurchasePreview = (purchase: SeatPurchase): SeatPurchasePreview => {
  const { accountId, customer, plan, isFetching } = useBillingPlan()
  const [createInvoicePreview, { data: previewData, isLoading: previewLoading }] = useCreateInvoicePreviewMutation()

  const showsAmounts = !hidesBillingInfo(customer)

  const previewPayload = useMemo(() => {
    const subscription = customer?.subscription
    if (!subscription || !chargesImmediately(customer) || purchase.general + purchase.sidecar === 0) {
      return null
    }
    return {
      ...(purchase.general > 0 ? { numberOfSeats: subscription.numberOfSeats + purchase.general } : {}),
      ...(purchase.sidecar > 0
        ? { additionalProductChanges: { assistant: { quantity: (subscription.additionalProducts.assistant?.quantity ?? 0) + purchase.sidecar } } }
        : {}),
    }
  }, [customer, purchase.general, purchase.sidecar])

  useEffect(() => {
    if (previewPayload) {
      createInvoicePreview({ accountId, payload: previewPayload })
    }
  }, [accountId, createInvoicePreview, previewPayload])

  const lines = useMemo(
    () =>
      (
        [
          [RoleType.SEAT, purchase.general],
          [RoleType.SIDECAR, purchase.sidecar],
        ] as [RoleType, number][]
      )
        .filter(([, quantity]) => quantity > 0)
        .map(([seatType, quantity]) => ({
          seatType,
          quantity,
          unitAmount: customer && showsAmounts ? seatUnitPrice(customer, plan, seatType) : null,
        })),
    [customer, plan, purchase.general, purchase.sidecar, showsAmounts],
  )

  return {
    lines,
    newRecurringFee: customer && showsAmounts ? newRecurringFee(customer, plan, purchase) : null,
    period: customer?.subscription?.period,
    renewsOn: customer?.subscription?.currentPeriodEnd,
    dueLabel: previewPayload ? (inFreeTrial(customer) ? 'endOfTrial' : 'today') : null,
    dueAmount: previewData?.data.amountDue ?? null,
    currency: previewData?.data.currency ?? 'usd',
    showsAmounts,
    isLoading: isFetching || previewLoading,
  }
}
